import React, { useEffect } from "react";
import CustomSplitColumnCard from "../../../misc/CustomSplitColumnCard";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { EnvironmentIcon, RobotVisibilityIcon } from "../../../../utils/icons/icons";
import EnvironmentCleaningConfiguration from "./EnvironmentCleaningConfiguration";
import VisibilityConfiguration from "./VisibilityConfiguration";
import { useTranslation } from "react-i18next";
import CustomSnackbar from "../../../Feedback/CustomSnackbar";
import {
  selectParamList,
  selectParamListRestState,
} from "../../../../store/robot-configurations/robot-configurations-selectors";
import HttpFetchStatus from "../../../../enums/api/http-fetch-status";
import ParamList from "../../../../interfaces/robot/configurations/param-list";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import {
  CleaningConfiguration,
  CleaningConfigurationLevel,
} from "../../../../interfaces/robot/cleaning-configurations/global-cleaning-configuration";
import getSaveErrorMessages from "../../../../functions/general/get-save-error-messages";
import {
  isMapRulesConfiguration,
  MapRulesConfiguration,
} from "../../../../interfaces/robot/cleaning-configurations/map-rules-configuration";
import ReadOnlyProtectedForm from "../../../Inputs/ReadOnlyProtectedForm";

const GlobalCleaningConfiguration: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { getParamList, patchParamList, patchParamListRestSet } = useSharedActions();

  const paramList = useSharedSelector((state) => selectParamList(state));
  const paramListRest = useSharedSelector((state) => selectParamListRestState(state));

  useEffect(() => {
    if (paramListRest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getParamList());
    }
  }, [dispatch, getParamList, paramListRest.getStatus]);

  const handleUpdateParamList = (patch: Partial<ParamList>) => {
    dispatch(patchParamList(patch));
  };

  const handleUpdateGlobalConfiguration = (patch: Partial<CleaningConfiguration> | Partial<MapRulesConfiguration>) => {
    if (paramList?.cleaningConfiguration == null) return;
    handleUpdateParamList(
      isMapRulesConfiguration(patch)
        ? {
            mapRulesConfiguration: {
              // Full extrapolation required to ensure no null fields
              beeper: patch.beeper ?? paramList.mapRulesConfiguration.beeper,
              drivingSpeed: patch.drivingSpeed ?? paramList.mapRulesConfiguration.drivingSpeed,
              frontLightsMode: patch.frontLightsMode ?? paramList.mapRulesConfiguration.frontLightsMode,
              warningLight: patch.warningLight ?? paramList.mapRulesConfiguration.warningLight,
              minObstacleHeightM: patch.minObstacleHeightM ?? paramList.mapRulesConfiguration.minObstacleHeightM,
            },
          }
        : {
            cleaningConfiguration: {
              // Full extrapolation required to ensure no null fields
              spatialProperty: patch.spatialProperty ?? paramList.cleaningConfiguration.spatialProperty,
              environmentPollution: patch.environmentPollution ?? paramList.cleaningConfiguration.environmentPollution,
              environmentHazard: patch.environmentHazard ?? paramList.cleaningConfiguration.environmentHazard,
            },
          },
    );
  };

  const snackbarCloseHandler = () => {
    dispatch(patchParamListRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null }));
  };

  return (
    <React.Fragment>
      <CustomSplitColumnCard
        title={t("configuration-cleaning")}
        content={[
          {
            left: (
              <Stack spacing={1} pr={2}>
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <EnvironmentIcon />
                  <Typography>{t("label-robot-environment-configuration")}</Typography>
                </Stack>
              </Stack>
            ),
            right: (
              <Stack>
                <EnvironmentCleaningConfiguration
                  configurationLevel={CleaningConfigurationLevel.Global}
                  globalCleaningConfiguration={paramList?.cleaningConfiguration}
                  globalMapRulesConfiguration={paramList?.mapRulesConfiguration}
                  onConfigurationChange={handleUpdateGlobalConfiguration}
                />
              </Stack>
            ),
          },
          {
            left: (
              <Stack spacing={1} pr={2}>
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <RobotVisibilityIcon />
                  <Typography>{t("label-robot-visibility-configuration")}</Typography>
                </Stack>
              </Stack>
            ),
            right: (
              <ReadOnlyProtectedForm>
                <Stack>
                  <VisibilityConfiguration
                    configurationLevel={CleaningConfigurationLevel.Global}
                    globalMapRulesConfiguration={paramList?.mapRulesConfiguration}
                    onMapRulesConfigurationChange={handleUpdateGlobalConfiguration}
                  />
                </Stack>
              </ReadOnlyProtectedForm>
            ),
          },
        ]}
      />
      <CustomSnackbar
        title={t("error-saving-cleaning-configuration")}
        open={paramListRest.patchStatus === HttpFetchStatus.Failed}
        onClose={snackbarCloseHandler}
        textMessages={getSaveErrorMessages(paramListRest.patchError)}
        severity="error"
      />
      <CustomSnackbar
        title={t("success-saving-cleaning-configuration")}
        severity="success"
        open={paramListRest.patchStatus === HttpFetchStatus.Success}
        onClose={snackbarCloseHandler}
      />
    </React.Fragment>
  );
};

export default GlobalCleaningConfiguration;
